import { MenuItem, Typography } from '@material-ui/core'
import React, { useState, useEffect } from 'react'
import {
  useMutation,
  useNotify,
  useListContext,
  sanitizeFetchType,
  GET_MANY,
  GetManyResult,
  useTranslate,
} from 'react-admin'
import { useHasAuthority } from '../../../hooks/useHasAuthority'
import { Authority } from '../../../../core/auth/Authority'
import { ResourceName } from '../../../../core/ResourceName'
import { ApiErrorDto } from '../../../../core/dto/error-handling/api-error.dto'
import {
  ProgressStatus,
  StateBulkProgressDialog,
} from '../../../common/StateBulkProgressDialog'
import { BulkActionProps } from '../../../common/BulkActionsProps'
import { TerminalLockDialog } from '../TerminalDialogs'
import { TerminalDto } from '../../../../core/dto/device/terminals/terminal.dto'

export const TerminalLockBulkButton = ({
  selectedIds,
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
  innerRef,
  asRecurringJob,
  ...props
}: BulkActionProps) => {
  const { loading, refetch } = useListContext<TerminalDto>()
  const notify = useNotify()
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()
  const [selectStateDialogOpen, setSelectStateDialogOpen] = useState(false)
  const [mutate, { loading: mutationLoading }] = useMutation()
  const [disabledId, setDisabledId] = useState(0)
  const [bulkStateDialogOpen, setBulkStateDialogOpen] = useState(false)
  const [bulkProgressStatus, setBulkProgressStatus] = useState<
    ProgressStatus[]
  >([])
  const updateBulkProgressStatus = (newStatus: ProgressStatus) => {
    const oldStatusIndex = bulkProgressStatus.findIndex(
      (s) => s.id === newStatus.id,
    )
    bulkProgressStatus.splice(oldStatusIndex, 1, newStatus)
  }
  const [selectedTerminals, setSelectedTerminals] = useState<TerminalDto[]>([])

  const handleMutation = async (
    terminal: TerminalDto,
    recurringJobName?: string,
    cronExpression?: string,
  ) => {
    let error
    let query
    if (!asRecurringJob) {
      query = {
        type: sanitizeFetchType('lock'),
        resource: ResourceName.TERMINALS,
        payload: {
          terminalId: terminal.id,
          disabledId,
        },
      }
    } else {
      query = {
        type: sanitizeFetchType('lock'),
        resource: ResourceName.TERMINALS,
        payload: {
          terminalId: terminal.id,
          disabledId,
          recurringJobName,
          cronExpression,
        },
      }
    }
    let result = null
    try {
      result = await mutate(query, {
        returnPromise: true,
      })
    } catch (err) {
      error = err
    }
    if (error) {
      updateBulkProgressStatus({
        id: terminal.id,
        status: 'error',
        error: error as ApiErrorDto,
      } as ProgressStatus)
      return error
    }
    updateBulkProgressStatus({
      id: terminal.id,
      status: 'success',
    } as ProgressStatus)
    return result
  }

  const handleLock = async (
    recurringJobName?: string,
    cronExpression?: string,
  ) => {
    setBulkStateDialogOpen(true)
    setSelectStateDialogOpen(false)
    const pendingRequests: Promise<any>[] = []
    const selectedTerminalResponse = (await mutate(
      {
        type: sanitizeFetchType(GET_MANY),
        resource: ResourceName.TERMINALS,
        payload: {
          ids: selectedIds,
        },
      },
      {
        returnPromise: true,
        onFailure: (err) => {
          notify(err?.message, 'error')
          setBulkStateDialogOpen(false)
        },
      },
    )) as GetManyResult<TerminalDto>
    if (!selectedTerminalResponse.data.length) {
      setBulkStateDialogOpen(false)
      return
    }
    selectedTerminalResponse.data.forEach((terminal: TerminalDto) => {
      pendingRequests.push(
        handleMutation(terminal, recurringJobName, cronExpression),
      )
      selectedTerminals.push(terminal)
      bulkProgressStatus.push({
        id: terminal.id,
        status: 'pending',
      } as ProgressStatus)
    })
    await Promise.all(pendingRequests)
    refetch()
  }

  useEffect(() => {
    if (!bulkStateDialogOpen) {
      setBulkProgressStatus([])
      setSelectedTerminals([])
    }
  }, [bulkStateDialogOpen])

  const handleDialogClose = () => {
    setSelectStateDialogOpen(false)
  }

  return (
    <MenuItem
      {...props}
      disabled={
        !selectedIds.length ||
        !hasAuthority(Authority.TERMINALS_SET_LOCK) ||
        loading ||
        mutationLoading
      }
      onClick={() => setSelectStateDialogOpen(true)}
      button
    >
      <Typography>
        {translate(
          !asRecurringJob
            ? 'resources.terminals.actions.lock'
            : 'resources.terminals.actions.lock-recurring',
        )}
      </Typography>
      <TerminalLockDialog
        open={selectStateDialogOpen}
        close={handleDialogClose}
        disabledId={disabledId}
        setDisabledId={setDisabledId}
        handleLock={handleLock}
        asRecurringJob={asRecurringJob}
      />
      <StateBulkProgressDialog
        open={bulkStateDialogOpen}
        close={() => setBulkStateDialogOpen(false)}
        elements={selectedTerminals}
        method="lock"
        statuses={bulkProgressStatus}
        resource={ResourceName.TERMINALS}
      />
    </MenuItem>
  )
}
